import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import emailjs from '@emailjs/browser';
import { Snackbar, Alert } from '@mui/material';
import SectionHeading from '../common/SectionHeading';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Bio } from '../../data/constants';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Section = styled.section`
  width: 100%;
  min-height: 100vh;
  padding: 120px 24px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.bg};
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 80px 16px;
  }
`;

const BackgroundGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(133, 76, 230, 0.12) 0%,
    rgba(0, 70, 209, 0.08) 50%,
    rgba(133, 76, 230, 0.06) 100%
  );
  pointer-events: none;
  z-index: 0;
`;

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 64px;
  width: 100%;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 48px;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const InfoCard = styled.div`
  border-radius: 24px;
  padding: 40px;
  background: rgba(20, 22, 35, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-4px);
    border-color: rgba(133, 76, 230, 0.3);
    box-shadow: 0 12px 48px rgba(133, 76, 230, 0.15);
  }
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    color: ${({ theme }) => theme.primary};
    font-size: 24px;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoItem = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  text-decoration: none;
  color: ${({ theme }) => theme.text_secondary};
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.text_primary};
    background: rgba(133, 76, 230, 0.1);
    border-color: rgba(133, 76, 230, 0.2);
    transform: translateX(4px);
  }
  
  svg {
    color: ${({ theme }) => theme.primary};
    font-size: 20px;
    flex-shrink: 0;
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_secondary};
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SocialButton = styled.a`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  background: rgba(255, 255, 255, 0.02);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
    background: rgba(133, 76, 230, 0.1);
    transform: translateY(-2px);
  }
  
  svg {
    font-size: 22px;
  }
`;

const FormSection = styled.div`
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const FormCard = styled.form`
  border-radius: 24px;
  padding: 48px;
  background: rgba(20, 22, 35, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 28px;
  
  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (min-width: 769px) {
    flex-direction: row;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  flex: 1;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
  transition: color 0.3s ease;
  
  ${Field}:focus-within & {
    color: ${({ theme }) => theme.primary};
  }
`;

const Input = styled.input`
  width: 100%;
  border-radius: 16px;
  padding: 16px 20px;
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: ${({ theme }) => theme.text_primary};
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.5;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    background: rgba(133, 76, 230, 0.05);
    box-shadow: 0 0 0 4px rgba(133, 76, 230, 0.1);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  border-radius: 16px;
  padding: 16px 20px;
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: ${({ theme }) => theme.text_primary};
  font-size: 16px;
  font-family: inherit;
  min-height: 160px;
  resize: vertical;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.5;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    background: rgba(133, 76, 230, 0.05);
    box-shadow: 0 0 0 4px rgba(133, 76, 230, 0.1);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled.button`
  border: none;
  border-radius: 16px;
  padding: 18px 32px;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(133, 76, 230, 0.95), rgba(0, 70, 209, 0.95));
  color: ${({ theme }) => theme.white};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(133, 76, 230, 1), rgba(0, 70, 209, 1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(133, 76, 230, 0.4);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    position: relative;
    z-index: 1;
  }
  
  span {
    position: relative;
    z-index: 1;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  position: relative;
  z-index: 1;
`;

const GradientFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 220px;
  background: ${({ theme }) => `
    linear-gradient(
      180deg,
      rgba(28, 28, 39, 0) 0%,
      ${theme.bgLight || theme.bg} 55%,
      rgba(8, 9, 17, 0.95) 100%
    )
  `};
  pointer-events: none;
  z-index: 0;
`;

const Contact = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const form = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    
    try {
      await emailjs.sendForm(
        'service_3ld6jda',
        'template_xwpiveu',
        form.current,
        '7YueSuHehBms5M33U'
      );
      setOpen(true);
      form.current.reset();
    } catch (err) {
      console.error('Error sending email:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="contact">
      <BackgroundGradient />
      <Wrapper>
        <SectionHeading
          eyebrow="Get in touch"
          title="Let's start a conversation"
          description="Have a project in mind or just want to chat? I'm always open to discussing new opportunities, creative ideas, or how we can bring your vision to life."
        />
        
        <ContentGrid>
          <ContactInfoSection>
            <InfoCard>
              <CardTitle>
                <EmailRoundedIcon />
                Contact Information
              </CardTitle>
              <InfoList>
                <InfoItem href="mailto:mfurquan.sal@gmail.com">
                  <EmailRoundedIcon />
                  <InfoContent>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>mfurquan.sal@gmail.com</InfoValue>
                  </InfoContent>
                </InfoItem>
                <InfoItem href="tel:+919951598025">
                  <PhoneIphoneIcon />
                  <InfoContent>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoValue>+91 995 159 8025</InfoValue>
                  </InfoContent>
                </InfoItem>
                <InfoItem href="https://www.linkedin.com/in/furquansal/" target="_blank" rel="noreferrer">
                  <LinkedInIcon />
                  <InfoContent>
                    <InfoLabel>LinkedIn</InfoLabel>
                    <InfoValue>Connect with me</InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoList>
            </InfoCard>

            <InfoCard>
              <CardTitle>
                <LocationOnIcon />
                Social Links
              </CardTitle>
              <SocialLinks>
                <SocialButton href={Bio.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                  <LinkedInIcon />
                </SocialButton>
                <SocialButton href={Bio.github} target="_blank" rel="noreferrer" aria-label="GitHub">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 .5C5.65.5.5 5.65.5 12a11.5 11.5 0 008.06 10.94c.59.11.8-.26.8-.58v-2.08c-3.28.71-3.97-1.59-3.97-1.59a3.15 3.15 0 00-1.33-1.75c-1.09-.75.08-.74.08-.74a2.48 2.48 0 011.81 1.22 2.52 2.52 0 003.44 1 2.51 2.51 0 01.75-1.58c-2.63-.3-5.39-1.32-5.39-5.89a4.6 4.6 0 011.22-3.19 4.29 4.29 0 01.12-3.15s1-.32 3.3 1.21a11.32 11.32 0 016 0c2.3-1.53 3.3-1.21 3.3-1.21a4.29 4.29 0 01.12 3.15 4.6 4.6 0 011.22 3.19c0 4.59-2.76 5.58-5.4 5.88a2.82 2.82 0 01.79 2.2v3.26c0 .32.21.7.81.58A11.5 11.5 0 0023.5 12C23.5 5.65 18.35.5 12 .5z" />
                  </svg>
                </SocialButton>
                <SocialButton href={Bio.twitter} target="_blank" rel="noreferrer" aria-label="Twitter">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </SocialButton>
                <SocialButton href={Bio.insta} target="_blank" rel="noreferrer" aria-label="Instagram">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </SocialButton>
              </SocialLinks>
            </InfoCard>
          </ContactInfoSection>

          <FormSection>
            <FormCard ref={form} onSubmit={handleSubmit}>
              <FieldGroup>
                <Field>
                  <Label htmlFor="from_name">Your Name</Label>
                  <Input
                    type="text"
                    id="from_name"
                    name="from_name"
                    placeholder="John Doe"
                    required
                    disabled={loading}
                  />
                </Field>
                <Field>
                  <Label htmlFor="from_email">Your Email</Label>
                  <Input
                    type="email"
                    id="from_email"
                    name="from_email"
                    placeholder="john@example.com"
                    required
                    disabled={loading}
                  />
                </Field>
              </FieldGroup>

              <Field>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder="What's this about?"
                  required
                  disabled={loading}
                />
              </Field>

              <Field>
                <Label htmlFor="message">Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  placeholder="Tell me about your project, idea, or just say hello..."
                  required
                  disabled={loading}
                />
              </Field>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <SendRoundedIcon />
                    <span>Send Message</span>
                  </>
                )}
              </SubmitButton>
            </FormCard>
          </FormSection>
        </ContentGrid>
      </Wrapper>
      <GradientFade aria-hidden="true" />

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: '100%' }}>
          Message sent successfully! I'll get back to you soon.
        </Alert>
      </Snackbar>

      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={() => setError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(false)} severity="error" sx={{ width: '100%' }}>
          Failed to send message. Please try again or contact me directly.
        </Alert>
      </Snackbar>
    </Section>
  );
};

export default Contact;
